/**
 * Operator-facing wording for the EIB audience reach estimate (eib_audience_quality_preview_list).
 * Pure functions, so the wording can be tested without a database.
 *
 * ⚠️ The preview's buckets OVERLAP. `recoverable_if_validated` (= `excluded.never_evaluated`) counts every
 * non-empty address with no NeverBounce verdict, INCLUDING addresses on the do-not-email list. And since
 * migration 394 a never-validated address on an EIB flight is checked before sending rather than dropped.
 * So the wording reports three EXCLUSIVE groups that sum to the frozen count:
 *   validated and deliverable  = will_reach_estimate
 *   not yet validated          = recoverable_if_validated   (outcome unknown: neither "excluded" nor "recoverable")
 *   will not be e-mailed       = excluded_total - not_yet_validated  (invalid, missing, or validated but do-not-email)
 * and never says a pending address is excluded "only" because it was never validated (external audit
 * 6bd7374d: on QA both such addresses were also suppressed).
 */
export interface EibAudienceQualityLike {
  available: boolean;
  will_reach_estimate?: number;
  excluded_total?: number;
  recoverable_if_validated?: number;
}

interface ReachGroups {
  deliverable: number;
  pending: number;
  excluded: number;
}

function reachGroups(
  frozen: number | null,
  quality: EibAudienceQualityLike | null | undefined,
): ReachGroups | null {
  if (
    frozen === null ||
    !quality?.available ||
    typeof quality.will_reach_estimate !== "number"
  ) {
    return null;
  }
  const deliverable = quality.will_reach_estimate;
  if (deliverable >= frozen) {
    return null;
  }
  const notDeliverable =
    typeof quality.excluded_total === "number"
      ? quality.excluded_total
      : frozen - deliverable;
  const pending = Math.min(
    Math.max(quality.recoverable_if_validated ?? 0, 0),
    notDeliverable,
  );
  return {
    deliverable,
    pending,
    excluded: Math.max(notDeliverable - pending, 0),
  };
}

const n = (v: number) => v.toLocaleString();
const EXCLUDED_WHY = "invalid, missing or do-not-email address";

/** Suffix for the "Audience frozen" launch step. */
export function audienceFreezeReachSuffix(
  frozenCount: number | null,
  quality: EibAudienceQualityLike | null | undefined,
): string {
  const g = reachGroups(frozenCount, quality);
  if (!g) {
    return "";
  }
  const parts = [`about ${n(g.deliverable)} validated and deliverable`];
  if (g.pending > 0) {
    parts.push(`${n(g.pending)} not yet validated`);
  }
  if (g.excluded > 0) {
    parts.push(`${n(g.excluded)} will not be e-mailed (${EXCLUDED_WHY})`);
  }
  return ` (${parts.join("; ")})`;
}

/** Sentence for the >25,000 confirmation prompt. */
export function confirmPromptReachLine(
  frozen: number | null,
  quality: EibAudienceQualityLike | null | undefined,
): string {
  const g = reachGroups(frozen, quality);
  if (!g) {
    return "";
  }
  const parts = [`about ${n(g.deliverable)} validated and deliverable`];
  if (g.pending > 0) {
    parts.push(`${n(g.pending)} not yet validated`);
  }
  if (g.excluded > 0) {
    parts.push(`${n(g.excluded)} will not be e-mailed (${EXCLUDED_WHY})`);
  }
  return ` Of those, ${parts.join("; ")}.`;
}
